import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, animate } from "framer-motion";
import { geoNaturalEarth1 } from "d3-geo";
import { ComposableMap, Geographies, Geography, Marker, Line } from "react-simple-maps";

const API = `${window.location.origin}/api`;

const GEO_URL = "/world-50m.json";
const W = 980;
const H = 500;
const RAGUSA = [14.72, 36.92];

const NODES = [
  // Sicilia
  { label: "Zona Palermo", coords: [13.36, 38.12], halo: 14 },
  { label: "Zona Catania", coords: [15.09, 37.5], halo: 14 },
  { label: "Zona Marzamemi", coords: [15.12, 36.74], halo: 12 },
  { label: "Zona Noto", coords: [15.07, 36.89], halo: 12 },
  // Italia
  { label: "Zona Capri", coords: [14.24, 40.55], halo: 14 },
  { label: "Zona Sorrento", coords: [14.37, 40.63], halo: 12 },
  { label: "Zona Roma", coords: [12.49, 41.9], halo: 18 },
  { label: "Zona Firenze", coords: [11.25, 43.77], halo: 14 },
  { label: "Zona Venezia", coords: [12.33, 45.44], halo: 14 },
  { label: "Zona Milano", coords: [9.19, 45.46], halo: 18 },
  // Mondo
  { label: "Monaco", coords: [7.42, 43.74], halo: 16 },
  { label: "Ibiza", coords: [1.43, 38.91], halo: 16 },
  { label: "Dubai", coords: [55.27, 25.2], halo: 20 },
  { label: "Sud Africa", coords: [18.42, -33.93], halo: 20 },
  { label: "Miami", coords: [-80.19, 25.76], halo: 20 },
  { label: "New York", coords: [-74.0, 40.71], halo: 20 },
  { label: "Canada", coords: [-79.38, 43.65], halo: 20 },
];

const ORIGIN_NODE = { label: "Ragusa — Altopiano Ibleo", sub: "La Maison · Origine" };

const VIEWS = {
  sicilia: { label: "Sicilia", center: [14.5, 37.35], zoom: 15 },
  italia: { label: "Italia", center: [12.2, 42.0], zoom: 5.2 },
  mondo: { label: "Mondo", center: [-5, 18], zoom: 1.15 },
};

export default function SocialProofMap() {
  const [view, setView] = useState("italia");
  const [active, setActive] = useState(null);
  const [nodes, setNodes] = useState(NODES);
  const wrapRef = useRef(null);
  const gRef = useRef(null);
  const markerRefs = useRef({});
  const cur = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/map/locations`)
      .then(({ data }) => {
        const list = data?.locations;
        if (Array.isArray(list) && list.length) {
          setNodes(
            list.map((d) => ({
              label: d.label,
              coords: [d.lon, d.lat],
              halo: d.halo || 16,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const projection = useMemo(
    () => geoNaturalEarth1().scale(175).center([0, 8]).translate([W / 2, H / 2]),
    []
  );

  useEffect(() => {
    const v = VIEWS[view];
    const [px, py] = projection(v.center);
    const target = { k: v.zoom, tx: W / 2 - v.zoom * px, ty: H / 2 - v.zoom * py };

    const apply = ({ k, tx, ty }) => {
      cur.current = { k, tx, ty };
      if (gRef.current)
        gRef.current.setAttribute("transform", `translate(${tx} ${ty}) scale(${k})`);
      Object.values(markerRefs.current).forEach(
        (m) => m && m.setAttribute("transform", `scale(${1 / k})`)
      );
    };

    if (!cur.current) {
      apply(target);
      return;
    }
    const from = { ...cur.current };
    const controls = animate(0, 1, {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (t) =>
        apply({
          k: from.k + (target.k - from.k) * t,
          tx: from.tx + (target.tx - from.tx) * t,
          ty: from.ty + (target.ty - from.ty) * t,
        }),
    });
    return () => controls.stop();
  }, [view, projection]);

  const showTip = (node) => (e) => {
    e.stopPropagation();
    const wrap = wrapRef.current?.getBoundingClientRect();
    const dot = e.currentTarget.getBoundingClientRect();
    if (!wrap) return;
    setActive({
      ...node,
      x: dot.left + dot.width / 2 - wrap.left,
      y: dot.top - wrap.top,
    });
  };

  const nodeMarker = (n, key, isOrigin = false) => (
    <Marker key={key} coordinates={isOrigin ? RAGUSA : n.coords}>
      <g
        ref={(el) => {
          markerRefs.current[key] = el;
          if (el && cur.current) el.setAttribute("transform", `scale(${1 / cur.current.k})`);
        }}
        style={{ cursor: "pointer" }}
        onMouseEnter={showTip(n)}
        onMouseLeave={() => setActive(null)}
        onClick={showTip(n)}
        data-testid={`map-node-${isOrigin ? "origin" : n.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {isOrigin ? (
          <>
            <circle r={22} fill="url(#vdi-halo)" />
            <circle r={5} fill="#0A0A0A" stroke="#C4A671" strokeWidth={1.4} />
            <circle r={1.8} fill="#C4A671" />
          </>
        ) : (
          <>
            <circle r={n.halo} fill="url(#vdi-halo)" />
            <circle r={n.halo * 0.55} fill="none" stroke="#C4A671" strokeWidth={0.5} strokeOpacity={0.5} />
            <circle r={2.4} fill="#C4A671" />
            <circle r={2.4} fill="none" stroke="#C4A671" strokeWidth={0.7} className="vdi-node-ring" />
          </>
        )}
      </g>
    </Marker>
  );

  return (
    <section
      id="presenza"
      data-testid="social-proof-section"
      className="relative bg-[#0A0A0A] text-[#F2EFE8] overflow-hidden"
      onClick={() => setActive(null)}
    >
      <div className="absolute inset-0 grain opacity-25" aria-hidden />
      <div className="relative max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-6 mb-6">
              <span className="section-num text-3xl" style={{ color: "#C4A671" }}>10</span>
              <div className="h-px w-12" style={{ background: "#C4A671" }} />
              <span className="overline text-[10px]" style={{ color: "#C4A671" }}>
                Presenza — Nelle Carte Di
              </span>
            </div>
            <h2
              className="font-serif-display leading-[0.95] text-[#F2EFE8]"
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.8rem)" }}
            >
              Da Ragusa,
              <br />
              <span className="italic font-light" style={{ color: "#C4A671" }}>
                verso il mondo.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[15.5px] leading-[1.9] font-light" style={{ color: "#C9C3B5" }}>
              Le nostre allocazioni viaggiano dall&apos;altopiano ibleo lungo direttrici
              che attraversano la Sicilia, l&apos;Italia e il mondo. Ogni alone sulla
              mappa rappresenta un&apos;area di influenza dove il nostro vino è presente
              — le identità dei singoli locali restano riservate, per politica della Maison.
            </p>
          </div>
        </motion.div>

        {/* View toggles */}
        <div className="flex items-center gap-2 mb-4" data-testid="map-view-toggles">
          {Object.entries(VIEWS).map(([key, v]) => (
            <button
              key={key}
              data-testid={`map-view-${key}`}
              onClick={(e) => { e.stopPropagation(); setView(key); setActive(null); }}
              className="px-4 py-2 text-[10px] uppercase tracking-[0.22em] border transition-colors duration-300"
              style={{
                borderColor: view === key ? "#C4A671" : "#2a2a2a",
                color: view === key ? "#C4A671" : "#7a7a7a",
                background: view === key ? "rgba(196,166,113,0.07)" : "transparent",
              }}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Map */}
        <div
          ref={wrapRef}
          className="relative border"
          style={{ borderColor: "#1a1a1a", background: "#0D0D0D" }}
          data-testid="social-proof-map"
        >
          <ComposableMap
            projection="geoNaturalEarth1"
            projectionConfig={{ scale: 175, center: [0, 8] }}
            width={W}
            height={H}
            style={{ width: "100%", height: "auto", display: "block" }}
          >
            <g ref={gRef}>
              <defs>
                <radialGradient id="vdi-halo">
                  <stop offset="0%" stopColor="#C4A671" stopOpacity="0.32" />
                  <stop offset="65%" stopColor="#C4A671" stopOpacity="0.09" />
                  <stop offset="100%" stopColor="#C4A671" stopOpacity="0" />
                </radialGradient>
              </defs>
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#171717"
                      stroke="#2d2d2d"
                      strokeWidth={0.5}
                      vectorEffect="non-scaling-stroke"
                      style={{
                        default: { outline: "none" },
                        hover: { outline: "none", fill: "#1b1b1b" },
                        pressed: { outline: "none" },
                      }}
                    />
                  ))
                }
              </Geographies>

              {nodes.map((n) => (
                <Line
                  key={n.label}
                  from={RAGUSA}
                  to={n.coords}
                  stroke="#C4A671"
                  strokeWidth={1}
                  strokeOpacity={0.45}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  className="vdi-arc"
                  pathLength={1}
                />
              ))}

              {nodes.map((n) =>
                nodeMarker(n, n.label)
              )}
              {nodeMarker(ORIGIN_NODE, "__origin", true)}
            </g>
          </ComposableMap>

          {active && (
            <div
              data-testid="map-tooltip"
              className="absolute z-10 pointer-events-none"
              style={{
                left: Math.min(Math.max(active.x, 90), (wrapRef.current?.clientWidth || W) - 90),
                top: Math.max(active.y - 14, 8),
                transform: "translate(-50%, -100%)",
              }}
            >
              <div
                className="px-4 py-3 text-center backdrop-blur-md"
                style={{
                  background: "rgba(10,10,10,0.88)",
                  border: "1px solid rgba(196,166,113,0.55)",
                  minWidth: 150,
                }}
              >
                <div className="overline text-[8.5px]" style={{ color: "#8B7A55" }}>
                  {active.sub || "Area di Distribuzione"}
                </div>
                <div className="font-serif-display italic text-[15px] mt-1" style={{ color: "#C4A671" }}>
                  {active.label}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mt-5 overline text-[9px]" style={{ color: "#7a7568" }}>
          Ogni alone rappresenta un&apos;area di distribuzione attiva · Le identità dei locali restano riservate
        </p>
      </div>
    </section>
  );
}
